/**
 * "Which bed did they go into?" (D69, owner's decision 6; critique R6 and R22): the one question asked before a Seed
 * vault plant leaves the vault, from either door that moves it (a Sow outside or Plant out tick, and Record a stage on
 * the plant card), answered in one sheet (WhichBedSheet, mounted once by WhichBedHost in the layout).
 *
 * askWhichBed opens the sheet and resolves with the answer: a bed, "Indoors" (Seeding only: it stays in the vault), or
 * null when the sheet was closed. The decisions are the server's: which plants need a bed (the brief's planting.askBed,
 * a plant's move_out) and whether a bed is required (400 TODO_BED_REQUIRED, BED_REQUIRED); this only reads them.
 *
 * Pure helpers, tested without a screen (whichBed.test.mjs): bedsToOffer, vaultPlantsTicked, stageAskOf.
 *
 * Where: features/today/lib/useTodoMarks (door 1), features/plant-list PlantStageLine (door 2), WhichBedHost.
 *
 * @module whichBed
 */

/** What the sheet is opened with. */
export interface BedAsk {
  /** A line under the title: the job's or the stage's name. */
  sub?: string | null;
  /** Seeding: offer "Indoors (stays in the Seed vault)" first. */
  indoors?: boolean;
}

/** What the sheet answers: a bed, Indoors, or null (closed). */
export type BedAnswer = { bedId: number } | { indoors: true } | null;

/** A bed as the sheet lists it. */
export interface BedChoice {
  id: number;
  name: string;
}

/** The question now open, and how to answer it. */
interface OpenAsk {
  ask: BedAsk;
  resolve: (answer: BedAnswer) => void;
}

/** The open question, or null. */
let current: OpenAsk | null = null;
/** Who draws the sheet. */
const listeners = new Set<() => void>();

/** Tells the host the question changed. */
function changed(): void {
  for (const listener of listeners) listener();
}

/**
 * Opens the sheet and waits for the answer. A question asked while another is open closes the first (answered null).
 *
 * @param ask - What to show.
 * @returns The answer.
 */
export function askWhichBed(ask: BedAsk = {}): Promise<BedAnswer> {
  current?.resolve(null);
  return new Promise<BedAnswer>((resolve) => {
    current = { ask, resolve };
    changed();
  });
}

/**
 * Answers the open question and closes the sheet.
 *
 * @param answer - The bed, Indoors, or null.
 */
export function answerWhichBed(answer: BedAnswer): void {
  const open = current;
  current = null;
  open?.resolve(answer);
  changed();
}

/**
 * The open question, for the host.
 *
 * @returns The question's details, or null.
 */
export function openWhichBed(): BedAsk | null {
  return current?.ask ?? null;
}

/**
 * Listens for the question to open or close.
 *
 * @param listener - Called on each change.
 * @returns Stops listening.
 */
export function subscribeWhichBed(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * The beds the sheet offers: the garden's live beds, the Seed vault left out (R22), in the server's order.
 *
 * @param locations - As GET /locations sends them.
 * @returns id and name of each.
 */
export function bedsToOffer(locations: ReadonlyArray<{ id: number; name: string; status: number; is_vault?: boolean }>): BedChoice[] {
  return locations.filter((bed) => Number(bed.status) === 1 && !bed.is_vault).map((bed) => ({ id: bed.id, name: bed.name }));
}

/**
 * The Seed vault plants a tick moves: the job's askBed, among the plants ticked (all of them for the whole job).
 *
 * @param askBed - The job's planting.askBed (the server's).
 * @param plantIds - The plants ticked, or undefined for the whole job.
 * @returns The ids; empty when nothing ticked is in the vault.
 */
export function vaultPlantsTicked(askBed: readonly number[] | undefined, plantIds?: readonly number[]): number[] {
  const asked = askBed ?? [];
  return plantIds ? asked.filter((id) => plantIds.includes(id)) : [...asked];
}

/**
 * What Record a stage asks for a plant: a bed (required), a bed or Indoors (Seeding), or nothing.
 *
 * @param moveOut - The plant's move_out (the server's), or null for a plant not in the vault.
 * @param stageId - The stage chosen.
 * @returns 'bed', 'indoors' or null.
 */
export function stageAskOf(moveOut: { bedAsked: number[]; indoors: number[] } | null | undefined, stageId: number): 'bed' | 'indoors' | null {
  if (!moveOut) return null;
  if (moveOut.bedAsked.includes(stageId)) return 'bed';
  if (moveOut.indoors.includes(stageId)) return 'indoors';
  return null;
}
